/**
 * Custom hook to manage worklist configuration
 */
import { useState, useEffect, useContext, useCallback } from 'react';
import { fetchWorklistFilterOptions } from '../utils/api';
import { policiesContext } from '../utils/common';

export interface UseWorklistConfigReturn {
    config: Record<string, any>;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

export function useWorklistConfig(isQualityAssessment: boolean): UseWorklistConfigReturn {
    const [config, setConfig] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { userContext } = useContext(policiesContext);

    const loadFilterOptions = useCallback(async () => {
        // Check if we have the required context
        if (!userContext?.activeContext?.tenantId || !userContext?.serviceLine?.name) {
            console.warn('[useWorklistConfig] Missing tenantId or serviceLine in userContext');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('[useWorklistConfig] Loading configuration for:', {
                tenantId: userContext.activeContext.tenantId,
                serviceLine: userContext.serviceLine.name
            });

            const options = await fetchWorklistFilterOptions(
                userContext.activeContext.tenantId,
                userContext.serviceLine.name,
                isQualityAssessment
            );

            setConfig(options);

            console.log('[useWorklistConfig] Configuration loaded successfully:', options);

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load worklist configuration';
            setError(errorMessage);
            console.error('[useWorklistConfig] Error loading configuration:', err);
        } finally {
            setLoading(false);
        }
    }, [userContext?.activeContext?.tenantId, userContext?.serviceLine?.name]);

    // Load configuration when context changes
    useEffect(() => {
        loadFilterOptions();
    }, [loadFilterOptions]);

    return {
        config,
        loading,
        error,
        refetch: loadFilterOptions
    };
}